/**
 * Turning what a person asked for into what Algolia is asked for.
 *
 * Pure, and separate from the network, because this is where the behaviours
 * that make search feel like search actually live — the phrase handling, the
 * department boost, the facet combination — and every one of them is worth a
 * test that does not need a live index.
 *
 * WHAT IS NOT HERE: the organization and the status restriction. Those are
 * signed into the search key by the backend and cannot be expressed, widened
 * or forgotten from this side. See functions/src/search/searchScope.ts.
 */

import { FACET_FIELDS, type FacetField, type FacetSelection } from './types';

export interface QueryInput {
  /** What the person typed. */
  query: string;
  facets?: FacetSelection;
  /** The searcher's own departments, used to lift their version of a shared
   *  procedure — never to hide anyone else's. */
  boostDepartmentIds?: readonly string[];
  hitsPerPage: number;
  page?: number;
  /**
   * Filters the policy library adds on top of the dropdowns: a view's
   * statuses (OR within a group), an owner. Same nesting as facetFilters.
   */
  extraFacetFilters?: string[][];
  /** Numeric conditions, ANDed: the library's review window. */
  numericFilters?: string[];
}

export interface AlgoliaParams {
  facetFilters: string[][];
  numericFilters?: string[];
  optionalFilters?: string[];
  facets: string[];
  hitsPerPage: number;
  page: number;
  advancedSyntax: boolean;
  removeWordsIfNoResults: 'none' | 'lastWords';
  maxValuesPerFacet: number;
}

/**
 * The selected dropdowns, as Algolia expects them.
 *
 * Nested arrays mean OR within a group and AND across groups. Only one value
 * per field is selectable today, so each group holds one term — but the shape
 * is the multi-select one so that adding it later is not a rewrite.
 *
 * Values are passed as array elements rather than spliced into a filter
 * string. A department called "Peds / NICU" would break the string form, and
 * would do it silently, returning zero rows rather than an error.
 */
export function facetFilters(selection: FacetSelection = {}): string[][] {
  const groups: string[][] = [];
  for (const field of FACET_FIELDS) {
    const value = selection[field];
    if (value) groups.push([`${field}:${value}`]);
  }
  return groups;
}

/**
 * Whether the query is a quoted phrase.
 *
 * Algolia's advanced syntax reads `"hand hygiene"` as an exact phrase, which
 * is what somebody typing quotes means. It is enabled per query rather than on
 * the index so that an unbalanced quote in an ordinary search does not change
 * how that search behaves.
 */
export function isPhraseQuery(query: string): boolean {
  const quotes = (query.match(/"/g) ?? []).length;
  return quotes >= 2 && quotes % 2 === 0;
}

export function buildParams(input: QueryInput): AlgoliaParams {
  const phrase = isPhraseQuery(input.query);
  const boosts = (input.boostDepartmentIds ?? []).filter(Boolean);

  return {
    facetFilters: [...facetFilters(input.facets), ...(input.extraFacetFilters ?? [])],
    ...(input.numericFilters?.length ? { numericFilters: input.numericFilters } : {}),

    // A ranking nudge, not a filter: Algolia sorts records matching these
    // higher and still returns the ones that do not. This is what makes a
    // nurse's "restraints" open with Nursing's procedure while Pharmacy's is
    // still one scroll away.
    ...(boosts.length ? { optionalFilters: boosts.map((id) => `departmentId:${id}`) } : {}),

    facets: [...FACET_FIELDS],

    hitsPerPage: input.hitsPerPage,
    page: input.page ?? 0,

    advancedSyntax: phrase,

    // With a phrase, every word is the point — dropping one to find something
    // would answer a question nobody asked. Without one, dropping trailing
    // words is what turns a five-word description into results.
    removeWordsIfNoResults: phrase ? 'none' : 'lastWords',

    // Enough to fill a dropdown without pulling every department in a system.
    maxValuesPerFacet: 50,
  };
}

/**
 * A short, human description of the facets in force, for the results header.
 */
export function describeFacets(
  selection: FacetSelection,
  labelFor: (field: FacetField, value: string) => string,
): string[] {
  return FACET_FIELDS.flatMap((field) => {
    const value = selection[field];
    return value ? [labelFor(field, value)] : [];
  });
}

export function activeFacetCount(selection: FacetSelection): number {
  return FACET_FIELDS.filter((f) => selection[f]).length;
}
